import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import { contactsAPI, inquiriesAPI } from '../services/api';

const ACCENT_COLOR = '#FF8C42';
const BORDER_COLOR = '#e2e8f0';
const TEXT_PRIMARY = '#1e293b';
const TEXT_SECONDARY = '#64748b';
const ALL_CONTACTS = 'ALL';
const ALL_STATUSES = 'ALL';
const DEFAULT_PAGE_SIZE = 20;

export const STATUS_OPTIONS = [
  {
    value: 'Pending',
    labelKey: 'pages.enquiry.status.pending',
    defaultLabel: 'Pending',
    color: '#b45309',
    bgColor: '#fef3c7',
    borderColor: '#fde68a',
    dotColor: '#f59e0b',
  },
  {
    value: 'InProgress',
    labelKey: 'pages.enquiry.status.inProgress',
    defaultLabel: 'In Progress',
    color: '#1d4ed8',
    bgColor: '#dbeafe',
    borderColor: '#bfdbfe',
    dotColor: '#3b82f6',
  },
  {
    value: 'Completed',
    labelKey: 'pages.enquiry.status.completed',
    defaultLabel: 'Completed',
    color: '#15803d',
    bgColor: '#dcfce7',
    borderColor: '#bbf7d0',
    dotColor: '#22c55e',
  },
  {
    value: 'Cancelled',
    labelKey: 'pages.enquiry.status.cancelled',
    defaultLabel: 'Cancelled',
    color: '#b91c1c',
    bgColor: '#fee2e2',
    borderColor: '#fecaca',
    dotColor: '#ef4444',
  },
];

export const normalizeStatus = (rawStatus) => {
  const s = String(rawStatus || '').trim().toLowerCase().replace(/[-_\s]/g, '');
  if (s === 'inprogress' || s === 'progress') return 'InProgress';
  if (s === 'completed' || s === 'complete') return 'Completed';
  if (s === 'cancelled' || s === 'canceled' || s === 'cancel') return 'Cancelled';
  return 'Pending';
};

export const getStatusConfig = (rawStatus) => {
  const norm = normalizeStatus(rawStatus);
  return STATUS_OPTIONS.find((opt) => opt.value === norm) || STATUS_OPTIONS[0];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const raw = String(dateStr).trim();
    const isoString = raw.endsWith('Z') || raw.includes('+') ? raw : `${raw}Z`;
    const d = new Date(isoString);
    if (isNaN(d.getTime())) {
      const fallback = new Date(raw);
      if (isNaN(fallback.getTime())) return raw;
      return fallback.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
};

const getArrayFromResponse = (responseData) => {
  const root = responseData?.data ?? responseData?.payload ?? responseData;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.records)) return root.records;
  return [];
};

const normalizeInquiry = (item) => {
  const id = item?.inquiryId ?? item?.inquiry_id ?? item?.id;
  const fullName = [item?.firstName, item?.surname || item?.lastName].filter(Boolean).join(' ').trim();
  const userName =
    item?.senderName ||
    item?.sender_name ||
    fullName ||
    item?.userName ||
    item?.customerName ||
    item?.name ||
    '—';
  const email =
    item?.senderEmail ||
    item?.sender_email ||
    item?.email ||
    item?.customerEmail ||
    item?.userEmail ||
    '—';
  const phoneNumber = item?.phoneNumber || item?.phone || item?.senderPhone || '';
  const message = item?.message || item?.inquiryMessage || item?.shortDescription || item?.description || '—';
  const shortDescription = item?.shortDescription || '';
  const contactTitle = item?.contactTitle || item?.contactName || '';
  const contactId = item?.contactId;
  const createdAtUtc = item?.createdAtUtc || item?.createdAt || null;
  const progressStatus = item?.progressStatus || item?.status || 'Pending';
  const progressStatusUpdatedAtUtc = item?.progressStatusUpdatedAtUtc || null;

  return {
    id,
    contactId,
    contactTitle,
    firstName: item?.firstName || '',
    surname: item?.surname || item?.lastName || '',
    userName,
    email,
    phoneNumber,
    message,
    shortDescription,
    createdAtUtc,
    progressStatus: normalizeStatus(progressStatus),
    progressStatusUpdatedAtUtc,
    raw: item,
  };
};

export default function Enquiry() {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(ALL_CONTACTS);
  const [selectedStatus, setSelectedStatus] = useState(ALL_STATUSES);
  const [searchTerm, setSearchTerm] = useState('');

  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [detailInquiry, setDetailInquiry] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const contactOptions = useMemo(
    () =>
      contacts.map((contact) => ({
        id: String(contact.id),
        title:
          contact?.title ||
          contact?.businessTitle ||
          contact?.name ||
          contact?.contactPerson ||
          `Contact #${contact.id}`,
      })),
    [contacts]
  );

  const fetchContacts = useCallback(async () => {
    try {
      const response = await contactsAPI.getContacts('', '', 0, 500);
      const list = getArrayFromResponse(response?.data);
      setContacts(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load contacts for enquiry filter:', error);
      setContacts([]);
    }
  }, []);

  const fetchEnquiries = useCallback(
    async (contactIdValue, statusValue, pageNum = page, pageSizeNum = rowsPerPage) => {
      setLoading(true);
      try {
        const contactId = contactIdValue === ALL_CONTACTS ? '' : contactIdValue;
        const status = statusValue === ALL_STATUSES ? '' : statusValue;
        const response = await inquiriesAPI.getInquiries(
          contactId,
          pageNum + 1,
          pageSizeNum,
          status
        );
        const data = response?.data?.data ?? response?.data?.payload ?? response?.data ?? {};
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.records)
              ? data.records
              : getArrayFromResponse(response?.data);

        const totalItems =
          data?.total ??
          data?.pagination?.total ??
          response?.data?.total ??
          (Array.isArray(list) ? list.length : 0);

        setTotal(Number(totalItems) || 0);

        const normalized = Array.isArray(list)
          ? list.map(normalizeInquiry).filter((item) => item.id !== undefined && item.id !== null)
          : [];

        setEnquiries(normalized);
      } catch (error) {
        console.error('Failed to load inquiries:', error);
        setEnquiries([]);
        setTotal(0);
        toast.error(t('pages.enquiry.loadError') || 'Failed to load inquiries');
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, t]
  );

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchEnquiries(selectedContactId, selectedStatus, page, rowsPerPage);
  }, [selectedContactId, selectedStatus, page, rowsPerPage, fetchEnquiries]);

  const handleStatusChange = async (inquiryId, newStatus) => {
    const item = enquiries.find((e) => e.id === inquiryId);
    if (!item || item.progressStatus === newStatus) return;

    const previousStatus = item.progressStatus;
    // Optimistic UI update
    setEnquiries((prev) =>
      prev.map((e) => (e.id === inquiryId ? { ...e, progressStatus: newStatus } : e))
    );
    if (detailInquiry && detailInquiry.id === inquiryId) {
      setDetailInquiry((prev) => (prev ? { ...prev, progressStatus: newStatus } : prev));
    }
    setUpdatingStatusId(inquiryId);

    try {
      await inquiriesAPI.updateInquiryStatus(inquiryId, newStatus);
      toast.success(t('pages.enquiry.statusUpdateSuccess') || 'Inquiry status updated successfully');
    } catch (error) {
      console.error('Failed to update inquiry status:', error);
      // Revert optimistic update
      setEnquiries((prev) =>
        prev.map((e) => (e.id === inquiryId ? { ...e, progressStatus: previousStatus } : e))
      );
      if (detailInquiry && detailInquiry.id === inquiryId) {
        setDetailInquiry((prev) => (prev ? { ...prev, progressStatus: previousStatus } : prev));
      }
      toast.error(t('pages.enquiry.statusUpdateError') || 'Failed to update inquiry status');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleOpenDelete = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeletingId(itemToDelete.id);
    try {
      await inquiriesAPI.deleteInquiry(itemToDelete.id);
      setEnquiries((prev) => prev.filter((e) => e.id !== itemToDelete.id));
      setTotal((prev) => Math.max(0, prev - 1));
      if (detailInquiry?.id === itemToDelete.id) {
        setDetailInquiry(null);
      }
      toast.success(t('pages.enquiry.deleteSuccess') || 'Inquiry deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete inquiry:', error);
      toast.error(t('pages.enquiry.deleteError') || 'Failed to delete inquiry');
    } finally {
      setDeletingId(null);
    }
  };

  // Filter inquiries client-side for immediate search and status safety
  const displayedEnquiries = useMemo(() => {
    let result = enquiries;
    if (selectedStatus !== ALL_STATUSES) {
      result = result.filter(
        (item) => normalizeStatus(item.progressStatus) === selectedStatus
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter(
        (item) =>
          item.userName.toLowerCase().includes(q) ||
          item.email.toLowerCase().includes(q) ||
          item.phoneNumber.toLowerCase().includes(q) ||
          item.contactTitle.toLowerCase().includes(q) ||
          item.message.toLowerCase().includes(q) ||
          item.shortDescription.toLowerCase().includes(q)
      );
    }
    return result;
  }, [enquiries, selectedStatus, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1440, mx: 'auto' }}>
      {/* Top Banner Card with Title on Left and Filters on Right */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', xl: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', xl: 'center' },
          gap: 2.5,
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid rgba(255, 140, 66, 0.25)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        {/* Left: Title & Subtitle */}
        <Box sx={{ minWidth: { xs: '100%', xl: 'auto' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: TEXT_PRIMARY }}
            >
              {t('pages.enquiry.title') || 'Inquiry'}
            </Typography>
            <Chip
              label={`${total} ${total === 1 ? 'Inquiry' : 'Inquiries'}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 140, 66, 0.12)',
                color: ACCENT_COLOR,
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500, color: TEXT_SECONDARY }}>
            {t('pages.enquiry.subtitle') || 'Review customer inquiries and manage incoming messages'}
          </Typography>
        </Box>

        {/* Right: Filters & Actions */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            width: { xs: '100%', xl: 'auto' },
            justifyContent: { xs: 'flex-start', xl: 'flex-end' },
          }}
        >
          {/* Search Box */}
          <TextField
            size="small"
            placeholder={t('pages.enquiry.filter.search') || 'Search inquiries...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 200, md: 220 },
              bgcolor: '#fff',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: TEXT_SECONDARY }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Contact Filter */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, bgcolor: '#fff' }}>
            <InputLabel id="enquiry-contact-filter">{t('pages.enquiry.filter.contact') || 'Contact'}</InputLabel>
            <Select
              labelId="enquiry-contact-filter"
              value={selectedContactId}
              label={t('pages.enquiry.filter.contact') || 'Contact'}
              onChange={(e) => {
                setSelectedContactId(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value={ALL_CONTACTS}>{t('pages.enquiry.filter.allContacts') || 'All Contacts'}</MenuItem>
              {contactOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, bgcolor: '#fff' }}>
            <InputLabel id="enquiry-status-filter">{t('pages.enquiry.filter.status') || 'Status'}</InputLabel>
            <Select
              labelId="enquiry-status-filter"
              value={selectedStatus}
              label={t('pages.enquiry.filter.status') || 'Status'}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value={ALL_STATUSES}>{t('pages.enquiry.filter.allStatuses') || 'All Statuses'}</MenuItem>
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: opt.dotColor }} />
                    <span>{t(opt.labelKey) || opt.defaultLabel}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Refresh Action button */}
          <Tooltip title="Refresh inquiries">
            <IconButton
              onClick={() => fetchEnquiries(selectedContactId, selectedStatus, page, rowsPerPage)}
              disabled={loading}
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 2,
                p: '8px',
                '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.08)', borderColor: ACCENT_COLOR },
              }}
            >
              <RefreshIcon fontSize="small" sx={{ color: ACCENT_COLOR }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Table / Content */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ color: ACCENT_COLOR }} />
          <Typography sx={{ mt: 2, color: TEXT_SECONDARY, fontWeight: 500 }}>
            {t('pages.enquiry.loading') || 'Loading inquiries...'}
          </Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${BORDER_COLOR}`,
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: isMobile ? 850 : '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc', borderBottom: `2px solid ${BORDER_COLOR}` }}>
                  <TableCell sx={{ fontWeight: 700, color: TEXT_PRIMARY, py: 1.5, pl: 2.5 }}>
                    {t('pages.enquiry.table.userName') || 'User / Sender'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: TEXT_PRIMARY, py: 1.5 }}>
                    {t('pages.enquiry.table.contact') || 'Contact'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: TEXT_PRIMARY, py: 1.5 }}>
                    {t('pages.enquiry.table.message') || 'Message'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: TEXT_PRIMARY, py: 1.5, minWidth: 140 }}>
                    {t('pages.enquiry.table.date') || 'Date'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: TEXT_PRIMARY, py: 1.5, minWidth: 160 }}>
                    {t('pages.enquiry.table.status') || 'Status'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: TEXT_PRIMARY, py: 1.5, pr: 2.5 }}>
                    {t('pages.enquiry.table.actions') || 'Actions'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedEnquiries.length ? (
                  displayedEnquiries.map((item) => {
                    const statusCfg = getStatusConfig(item.progressStatus);
                    const isUpdating = updatingStatusId === item.id;

                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.04)' },
                          borderBottom: `1px solid ${BORDER_COLOR}`,
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        {/* Sender info */}
                        <TableCell sx={{ pl: 2.5, py: 1.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontWeight: 700, color: TEXT_PRIMARY, fontSize: '0.875rem' }}>
                              {item.userName}
                            </Typography>
                            {item.email && item.email !== '—' && (
                              <Typography sx={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>
                                {item.email}
                              </Typography>
                            )}
                            {item.phoneNumber && (
                              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                📞 {item.phoneNumber}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* Contact title */}
                        <TableCell sx={{ py: 1.5 }}>
                          {item.contactTitle ? (
                            <Chip
                              label={item.contactTitle}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: 'rgba(255, 140, 66, 0.3)',
                                color: TEXT_PRIMARY,
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                bgcolor: '#fafafa',
                                maxWidth: 180,
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '0.8125rem', color: TEXT_SECONDARY }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Message preview */}
                        <TableCell
                          sx={{
                            py: 1.5,
                            maxWidth: 320,
                            cursor: 'pointer',
                          }}
                          onClick={() => setDetailInquiry(item)}
                        >
                          <Tooltip title="Click to view full inquiry details" arrow>
                            <Box>
                              {item.shortDescription && (
                                <Typography
                                  sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                    color: TEXT_PRIMARY,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.shortDescription}
                                </Typography>
                              )}
                              <Typography
                                sx={{
                                  fontSize: '0.8125rem',
                                  color: TEXT_SECONDARY,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                }}
                              >
                                {item.message}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>

                        {/* Created Date */}
                        <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                          <Typography sx={{ fontSize: '0.8125rem', color: TEXT_SECONDARY }}>
                            {formatDate(item.createdAtUtc)}
                          </Typography>
                        </TableCell>

                        {/* Status Select dropdown */}
                        <TableCell sx={{ py: 1.5 }}>
                          <FormControl size="small" sx={{ minWidth: 145 }}>
                            <Select
                              value={item.progressStatus}
                              onChange={(e) => handleStatusChange(item.id, e.target.value)}
                              disabled={isUpdating}
                              size="small"
                              sx={{
                                borderRadius: 5,
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                height: 32,
                                color: statusCfg.color,
                                bgcolor: statusCfg.bgColor,
                                border: `1px solid ${statusCfg.borderColor}`,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                                '& .MuiSelect-select': {
                                  py: 0.5,
                                  pl: 1.5,
                                  pr: '28px !important',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                },
                                '&:hover': {
                                  bgcolor: statusCfg.bgColor,
                                  filter: 'brightness(0.97)',
                                },
                              }}
                              renderValue={(selectedVal) => {
                                const cfg = getStatusConfig(selectedVal);
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isUpdating ? (
                                      <CircularProgress size={12} sx={{ color: cfg.color }} />
                                    ) : (
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          bgcolor: cfg.dotColor,
                                        }}
                                      />
                                    )}
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 700,
                                        color: cfg.color,
                                      }}
                                    >
                                      {t(cfg.labelKey) || cfg.defaultLabel}
                                    </Typography>
                                  </Box>
                                );
                              }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <MenuItem
                                  key={opt.value}
                                  value={opt.value}
                                  sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: opt.dotColor,
                                    }}
                                  />
                                  <span style={{ color: opt.color }}>
                                    {t(opt.labelKey) || opt.defaultLabel}
                                  </span>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right" sx={{ pr: 2.5, py: 1.5, whiteSpace: 'nowrap' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setDetailInquiry(item)}
                              sx={{
                                color: TEXT_SECONDARY,
                                mr: 0.5,
                                '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.12)', color: ACCENT_COLOR },
                              }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('common.delete') || 'Delete'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(item)}
                              disabled={deletingId === item.id}
                              sx={{
                                color: '#ef4444',
                                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                              }}
                            >
                              {deletingId === item.id ? (
                                <CircularProgress size={16} sx={{ color: '#ef4444' }} />
                              ) : (
                                <DeleteOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: TEXT_SECONDARY }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600, color: TEXT_SECONDARY }}>
                          {t('pages.enquiry.empty') || 'No inquiries found'}
                        </Typography>
                        {(searchTerm || selectedContactId !== ALL_CONTACTS || selectedStatus !== ALL_STATUSES) && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              setSearchTerm('');
                              setSelectedContactId(ALL_CONTACTS);
                              setSelectedStatus(ALL_STATUSES);
                            }}
                            sx={{ mt: 1, color: ACCENT_COLOR, textTransform: 'none' }}
                          >
                            Reset filters
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={total || displayedEnquiries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${BORDER_COLOR}`,
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.8125rem',
                color: TEXT_SECONDARY,
              },
            }}
          />
        </Paper>
      )}

      {/* Inquiry Detail Dialog */}
      <Dialog
        open={Boolean(detailInquiry)}
        onClose={() => setDetailInquiry(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
          },
        }}
      >
        {detailInquiry && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: TEXT_PRIMARY }}>
                  {t('pages.enquiry.details.title') || 'Inquiry Details'}
                </Typography>
                {(() => {
                  const cfg = getStatusConfig(detailInquiry.progressStatus);
                  return (
                    <Chip
                      label={t(cfg.labelKey) || cfg.defaultLabel}
                      size="small"
                      sx={{
                        bgcolor: cfg.bgColor,
                        color: cfg.color,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        border: `1px solid ${cfg.borderColor}`,
                      }}
                    />
                  );
                })()}
              </Box>
              <IconButton size="small" onClick={() => setDetailInquiry(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ py: 2.5 }}>
              {/* Meta Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: `1px solid ${BORDER_COLOR}`,
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PersonOutlineIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY }}>
                      {t('pages.enquiry.details.sender') || 'Sender'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: TEXT_PRIMARY, fontSize: '0.9rem', pl: 3.25 }}>
                    {detailInquiry.userName}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <BusinessOutlinedIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY }}>
                      {t('pages.enquiry.details.contact') || 'Contact'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: '0.9rem', pl: 3.25 }}>
                    {detailInquiry.contactTitle || '—'}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY }}>
                      {t('pages.enquiry.details.email') || 'Email'}
                    </Typography>
                  </Box>
                  <Typography
                    component="a"
                    href={detailInquiry.email && detailInquiry.email !== '—' ? `mailto:${detailInquiry.email}` : undefined}
                    sx={{
                      fontSize: '0.875rem',
                      color: detailInquiry.email && detailInquiry.email !== '—' ? ACCENT_COLOR : TEXT_SECONDARY,
                      pl: 3.25,
                      textDecoration: 'none',
                      wordBreak: 'break-all',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {detailInquiry.email}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PhoneOutlinedIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY }}>
                      {t('pages.enquiry.details.phone') || 'Phone'}
                    </Typography>
                  </Box>
                  <Typography
                    component="a"
                    href={detailInquiry.phoneNumber ? `tel:${detailInquiry.phoneNumber}` : undefined}
                    sx={{
                      fontSize: '0.875rem',
                      color: detailInquiry.phoneNumber ? TEXT_PRIMARY : TEXT_SECONDARY,
                      fontWeight: 600,
                      pl: 3.25,
                      textDecoration: 'none',
                    }}
                  >
                    {detailInquiry.phoneNumber || '—'}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY }}>
                      {t('pages.enquiry.details.date') || 'Created Date'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8125rem', color: TEXT_PRIMARY, pl: 3.25 }}>
                    {formatDate(detailInquiry.createdAtUtc)}
                  </Typography>
                </Box>

                {detailInquiry.progressStatusUpdatedAtUtc && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY }}>
                        {t('pages.enquiry.details.updatedAt') || 'Last Updated'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: TEXT_PRIMARY, pl: 3.25 }}>
                      {formatDate(detailInquiry.progressStatusUpdatedAtUtc)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Status Update Control inside modal */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  border: `1px solid ${BORDER_COLOR}`,
                }}
              >
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: TEXT_PRIMARY }}>
                  {t('pages.enquiry.details.status') || 'Update Status'}:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={detailInquiry.progressStatus}
                    onChange={(e) => handleStatusChange(detailInquiry.id, e.target.value)}
                    disabled={updatingStatusId === detailInquiry.id}
                    sx={{
                      borderRadius: 2,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: opt.dotColor }} />
                          <span style={{ color: opt.color, fontWeight: 600 }}>
                            {t(opt.labelKey) || opt.defaultLabel}
                          </span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Short Description (if present) */}
              {detailInquiry.shortDescription &&
                detailInquiry.shortDescription !== detailInquiry.message && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: TEXT_SECONDARY, mb: 0.5, textTransform: 'uppercase' }}>
                      {t('pages.enquiry.details.shortDesc') || 'Short Description'}
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                        border: `1px solid ${BORDER_COLOR}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }}>
                        {detailInquiry.shortDescription}
                      </Typography>
                    </Paper>
                  </Box>
                )}

              {/* Full Message */}
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: TEXT_SECONDARY, mb: 0.5, textTransform: 'uppercase' }}>
                  {t('pages.enquiry.details.message') || 'Message'}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    border: `1px solid ${BORDER_COLOR}`,
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: TEXT_PRIMARY,
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                    }}
                  >
                    {detailInquiry.message}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  handleOpenDelete(detailInquiry);
                }}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {t('common.delete') || 'Delete'}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setDetailInquiry(null)}
                sx={{
                  bgcolor: ACCENT_COLOR,
                  '&:hover': { bgcolor: '#e67329' },
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                }}
              >
                {t('common.close') || 'Close'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingId && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: TEXT_PRIMARY }}>
          {t('pages.enquiry.deleteDialog.title') || 'Delete Inquiry'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.9rem' }}>
            {t('pages.enquiry.deleteDialog.confirm') ||
              'Are you sure you want to delete this inquiry? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={Boolean(deletingId)}
            sx={{ borderRadius: 2, textTransform: 'none', color: TEXT_SECONDARY }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={Boolean(deletingId)}
            sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
          >
            {deletingId ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('common.delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
